import React, { useMemo, useState } from 'react';
import {
  Check,
  ChevronDown,
  ClipboardCheck,
  Clock3,
  CircleX,
  RotateCcw,
} from 'lucide-react';

const SectionStatusGrid = ({ sections = [] }) => {
  const [isExpanded, setIsExpanded] = useState(true);

  const validSections = useMemo(
    () => sections.filter((section) => section?.id != null),
    [sections],
  );

  const completedCount = validSections.filter(
    (section) => section.status === 'APPROVED',
  ).length;

  const totalCount = validSections.length;

  if (!totalCount) return null;

  const getStatusConfig = (status) => {
    switch (status) {
      case 'APPROVED':
      case 'COMPLETED':
        return {
          label: 'Completed',
          icon: Check,
          checkbox:
            'border-emerald-500 bg-emerald-500 text-white shadow-[0_2px_6px_rgba(16,185,129,0.16)]',
          badge: 'border-emerald-100 bg-emerald-50 text-emerald-600',
        };

      case 'IN_REVIEW':
      case 'IN_PROGRESS':
        return {
          label: 'In Review',
          icon: Clock3,
          checkbox: 'border-blue-400 bg-blue-50 text-blue-600',
          badge: 'border-blue-100 bg-blue-50 text-blue-600',
        };

      case 'REJECTED':
        return {
          label: 'Rejected',
          icon: CircleX,
          checkbox: 'border-red-300 bg-red-50 text-red-500',
          badge: 'border-red-100 bg-red-50 text-red-600',
        };

      case 'RESUBMIT':
      case 'REQUIRES_RESUBMISSION':
        return {
          label: 'Resubmit',
          icon: RotateCcw,
          checkbox: 'border-amber-300 bg-amber-50 text-amber-600',
          badge: 'border-amber-100 bg-amber-50 text-amber-600',
        };

      case 'PENDING':
      case 'NOT_STARTED':
      default:
        return {
          label: 'Pending',
          icon: null,
          checkbox:
            'border-slate-300 bg-white text-transparent group-hover:border-[#375AE7]/40',
          badge: 'border-slate-200 bg-slate-50 text-slate-400',
        };
    }
  };

  return (
    <section className="w-full">
      {/* Header */}
      <button
        type="button"
        onClick={() => setIsExpanded((prev) => !prev)}
        className="
          flex w-full items-center justify-between
          border-b border-slate-200
          py-3
          text-left
          transition-colors
          hover:border-slate-300
        "
      >
        <div className="flex min-w-0 items-center gap-3">
          <div
            className="
              flex h-8 w-8 shrink-0
              items-center justify-center
              rounded-lg
              border border-[#375AE7]/10
              bg-[#375AE7]/5
            "
          >
            <ClipboardCheck
              size={17}
              strokeWidth={2}
              className="text-[#375AE7]"
            />
          </div>

          <div className="flex items-center gap-2.5">
            <h3 className="text-md font-medium mb-2">Application Check List</h3>

            <span
              className="
                rounded-full
                border border-slate-200
                bg-slate-50
                px-2 py-0.5
                text-[11px]
                font-medium
                text-slate-500
              "
            >
              {completedCount}/{totalCount}
            </span>
          </div>
        </div>

        <ChevronDown
          size={17}
          strokeWidth={2}
          className={`
            shrink-0
            text-slate-400
            transition-transform duration-200
            ${isExpanded ? 'rotate-180' : ''}
          `}
        />
      </button>

      {/* Checklist */}
      <div
        className={`
          overflow-hidden
          transition-all duration-300 ease-in-out
          ${isExpanded ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}
        `}
      >
        <div className="grid grid-cols-1 gap-x-10 sm:grid-cols-2">
          {validSections.map((section, index) => {
            const config = getStatusConfig(section.status);
            const StatusIcon = config.icon;

            return (
              <div
                key={section.id ?? `${section.section}-${index}`}
                className="
                  group
                  flex min-w-0
                  items-center
                  gap-3
                  border-b border-slate-100
                  py-3
                  sm:px-1
                "
              >
                {/* Status icon */}
                <div
                  className={`
                    flex h-[22px] w-[22px]
                    shrink-0
                    items-center justify-center
                    rounded-[6px]
                    border
                    transition-all duration-200
                    ${config.checkbox}
                  `}
                >
                  {StatusIcon && <StatusIcon size={12} strokeWidth={3} />}
                </div>

                {/* Section name */}
                <span
                  className="
                    min-w-0 flex-1 truncate
                    text-[13px]
                    font-medium
                    tracking-[-0.01em]
                    text-slate-700
                  "
                  title={section.name}
                >
                  {section.name}
                </span>

                {/* Status */}
                <span
                  className={`
                    shrink-0
                    rounded-full
                    border
                    px-2 py-1
                    text-[9px]
                    font-semibold
                    tracking-wide
                    ${config.badge}
                  `}
                >
                  {config.label}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default SectionStatusGrid;
